import React, { useState } from 'react';
import { canvasService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  PenLine, FileText, Star, AlertCircle, Loader2,
  ChevronDown, ChevronUp, CheckCircle2, XCircle,
  MessageSquare, RotateCcw, User,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS = ['SUBMITTED', 'REVISION_REQUESTED', 'GRADED', 'ALL'];

const statusStyle = {
  SUBMITTED:          'bg-amber-50 text-amber-700 border-amber-200',
  REVISION_REQUESTED: 'bg-orange-50 text-orange-700 border-orange-200',
  GRADED:             'bg-forest-50 text-forest-700 border-forest-200',
  PENDING:            'bg-cream-100 text-forest-400 border-cream-200',
};

// ── Submission Card ───────────────────────────────────────────
const SubmissionCard = ({ submission, onGraded }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm]         = useState({ score: '', feedback: '', status: 'GRADED' });
  const [saving, setSaving]     = useState(false);

  const clientName    = `${submission.client?.user?.firstName ?? ''} ${submission.client?.user?.lastName ?? ''}`.trim() || 'Client';
  const stageName     = submission.canvasActivity?.stage?.name ?? 'Stage';
  const levelName     = submission.canvasActivity?.stage?.level?.name ?? '';
  const activityTitle = submission.canvasActivity?.title ?? 'Canvas Activity';
  const maxScore      = submission.canvasActivity?.maxScore ?? 100;
  const passingScore  = submission.canvasActivity?.passingScore ?? 60;
  const isGraded      = submission.status === 'GRADED';
  const isRevision    = submission.status === 'REVISION_REQUESTED';

  const submittedAt = submission.submittedAt
    ? new Date(submission.submittedAt).toLocaleDateString('en-GH', { dateStyle: 'medium' })
    : '—';

  const parseFileUrls = (fileUrls) => {
    if (Array.isArray(fileUrls)) return fileUrls;
    if (typeof fileUrls === 'string') { try { return JSON.parse(fileUrls); } catch { return []; } }
    return [];
  };

  const handleGrade = async () => {
    if (!form.score) { toast.error('Please enter a score'); return; }
    const score = Number(form.score);
    if (isNaN(score) || score < 0 || score > maxScore) {
      toast.error(`Score must be between 0 and ${maxScore}`); return;
    }
    if (form.status === 'REVISION_REQUESTED' && !form.feedback.trim()) {
      toast.error('Please provide feedback explaining what needs to be revised'); return;
    }
    setSaving(true);
    try {
      await canvasService.gradeSubmission(submission.id, {
        score,
        feedback: form.feedback,
        status:   form.status,
      });
      toast.success(
        form.status === 'GRADED'
          ? `✅ Submission passed! Client has been notified.`
          : `🔄 Revision requested. Client has been notified.`
      );
      onGraded();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save grade');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="p-5 flex items-start gap-4">
        <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0">
          {clientName[0]?.toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-sans font-semibold text-forest-900 text-sm">{clientName}</h4>
              <div className="text-xs text-forest-500 mt-0.5">
                <span className="font-medium text-forest-700">{activityTitle}</span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-forest-400">
                {levelName && <span className="bg-forest-50 border border-forest-100 px-2 py-0.5 rounded-full text-forest-600">{levelName}</span>}
                <span className="bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full text-blue-600">{stageName}</span>
                <span>Submitted: {submittedAt}</span>
                {submission.revisionCount > 0 && (
                  <span className="flex items-center gap-1 text-orange-500">
                    <RotateCcw size={10} /> Rev. #{submission.revisionCount}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isGraded && submission.score != null && (
                <div className="flex items-center gap-1">
                  <Star size={13} className="fill-gold-400 text-gold-400" />
                  <span className="font-serif font-bold text-forest-900">{submission.score}</span>
                  <span className="text-xs text-forest-400">/{maxScore}</span>
                </div>
              )}
              <span className={`text-[10px] font-medium border px-2.5 py-1 rounded-full ${statusStyle[submission.status] ?? ''}`}>
                {submission.status === 'REVISION_REQUESTED' ? 'REVISION NEEDED' : submission.status}
              </span>
              <button onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-50 transition-colors">
                {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-cream-100 p-5 space-y-5">

          {/* Activity instructions (context for expert) */}
          {submission.canvasActivity?.instructions && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-widest mb-2">
                Activity Instructions (what client was asked to do)
              </p>
              <p className="text-xs text-blue-700 leading-relaxed whitespace-pre-line">
                {submission.canvasActivity.instructions}
              </p>
            </div>
          )}

          {/* Client's submission text */}
          {submission.submissionText && (() => {
            let parsed = null;
            try { parsed = JSON.parse(submission.submissionText); } catch {}
            return (
              <div>
                <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <User size={12} /> Client's Response
                </p>
                {Array.isArray(parsed) && parsed[0]?.question ? (
                  <div className="space-y-4">
                    {parsed.map((qa, i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <span className="w-5 h-5 bg-forest-800 text-cream-50 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                          <p className="text-xs font-semibold text-forest-700">{qa.question}</p>
                        </div>
                        <div className="ml-7 bg-cream-50 border border-cream-100 rounded-xl px-4 py-3">
                          <p className="text-sm text-forest-700 leading-relaxed whitespace-pre-line">
                            {qa.answer || <span className="text-forest-300 italic">No answer provided</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-cream-50 border border-cream-100 rounded-xl p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm text-forest-700 leading-relaxed whitespace-pre-wrap">{submission.submissionText}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Files */}
          {parseFileUrls(submission.fileUrls).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <FileText size={12} /> Submitted Files
              </p>
              <div className="flex flex-wrap gap-2">
                {parseFileUrls(submission.fileUrls).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs bg-white border border-forest-200 text-forest-700 px-3 py-1.5 rounded-full hover:bg-forest-50 transition-colors">
                    <FileText size={11} /> File {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Previous feedback (if revision was requested before) */}
          {isGraded && submission.feedback && (
            <div className="bg-forest-50 border border-forest-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <MessageSquare size={12} /> Your Feedback
              </p>
              <p className="text-sm text-forest-700 leading-relaxed whitespace-pre-wrap">{submission.feedback}</p>
              {submission.score != null && (
                <div className="mt-3 flex items-center gap-2">
                  <Star size={14} className="fill-gold-400 text-gold-400" />
                  <span className="font-serif font-bold text-forest-900 text-lg">{submission.score}</span>
                  <span className="text-xs text-forest-400">/ {maxScore}</span>
                  {submission.score >= passingScore
                    ? <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle2 size={10} /> Passed
                      </span>
                    : <span className="text-xs text-rust-600 bg-rust-50 border border-rust-200 px-2 py-0.5 rounded-full">
                        Not Passed
                      </span>}
                </div>
              )}
            </div>
          )}

          {/* Previous revision feedback shown to help expert remember what they said */}
          {isRevision && submission.feedback && (
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <RotateCcw size={12} /> Your Previous Feedback (client was asked to revise)
              </p>
              <p className="text-sm text-orange-700 leading-relaxed whitespace-pre-wrap">{submission.feedback}</p>
            </div>
          )}

          {/* Grade form — show if not yet graded (or if revision was requested = can re-grade) */}
          {!isGraded && (
            <div className="space-y-4 pt-2 border-t border-cream-100">
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest flex items-center gap-1.5">
                <PenLine size={12} />
                {isRevision ? 'Re-grade Resubmission' : 'Grade Submission'}
              </p>

              {/* Decision first — prominent */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setForm(f => ({ ...f, status: 'GRADED' }))}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    form.status === 'GRADED'
                      ? 'bg-forest-600 border-forest-600 text-white'
                      : 'bg-white border-cream-200 text-forest-600 hover:border-forest-400'
                  }`}>
                  <CheckCircle2 size={16} /> Pass
                </button>
                <button
                  onClick={() => setForm(f => ({ ...f, status: 'REVISION_REQUESTED' }))}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    form.status === 'REVISION_REQUESTED'
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-white border-cream-200 text-orange-500 hover:border-orange-300'
                  }`}>
                  <RotateCcw size={16} /> Request Revision
                </button>
              </div>

              {/* Score */}
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">
                  Score * <span className="text-forest-400 font-normal">(passing: {passingScore}/{maxScore})</span>
                </label>
                <input
                  type="number"
                  value={form.score}
                  onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
                  min="0" max={maxScore}
                  placeholder={`0 – ${maxScore}`}
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500"
                />
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">
                  {form.status === 'REVISION_REQUESTED'
                    ? 'Feedback * — explain what needs to be corrected'
                    : 'Feedback (optional) — encourage the client'}
                </label>
                <textarea
                  value={form.feedback}
                  onChange={e => setForm(f => ({ ...f, feedback: e.target.value }))}
                  rows={5}
                  placeholder={form.status === 'REVISION_REQUESTED'
                    ? "Clearly explain what was wrong and what the client needs to correct or improve before resubmitting..."
                    : "Well done! Highlight what they did well and any suggestions for the next stage..."}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none"
                />
                {form.status === 'REVISION_REQUESTED' && !form.feedback.trim() && (
                  <p className="text-xs text-orange-500 mt-1">Feedback is required when requesting a revision</p>
                )}
              </div>

              <button
                onClick={handleGrade}
                disabled={saving || !form.score}
                className={`w-full justify-center flex items-center gap-2 py-3 rounded-full font-semibold text-sm transition-all disabled:opacity-60 ${
                  form.status === 'GRADED'
                    ? 'bg-forest-700 hover:bg-forest-800 text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}>
                {saving
                  ? <><Loader2 size={14} className="animate-spin" /> Saving...</>
                  : form.status === 'GRADED'
                    ? <><CheckCircle2 size={14} /> Submit Grade & Pass</>
                    : <><RotateCcw size={14} /> Send Revision Request</>}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const ExpertSubmissionsPage = () => {
  const [activeTab,  setActiveTab]  = useState('SUBMITTED');
  const [refetchKey, setRefetchKey] = useState(0);

  const params = { page: 1, limit: 20 };
  if (activeTab !== 'ALL') params.status = activeTab;

  const { data, loading, error } = useApi(
    () => canvasService.getSubmissionsToGrade(params),
    [activeTab, refetchKey],
  );

  const submissions = Array.isArray(data) ? data
    : Array.isArray(data?.submissions) ? data.submissions
    : Array.isArray(data?.data)        ? data.data : [];

  const counts = {
    SUBMITTED:          submissions.filter(s => s.status === 'SUBMITTED').length,
    REVISION_REQUESTED: submissions.filter(s => s.status === 'REVISION_REQUESTED').length,
    GRADED:             submissions.filter(s => s.status === 'GRADED').length,
  };

  return (
    <DashboardLayout title="Canvas Submissions">
      <div className="max-w-3xl space-y-5">

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-xs text-blue-700 flex items-start gap-2.5">
          <PenLine size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>Reviewing submissions:</strong> For each stage, you can Pass the submission or Request a Revision with feedback.
            The client will be notified and can resubmit. Once all stages are passed, the client can book a live session.
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-all flex items-center gap-1.5 ${
                activeTab === t
                  ? 'bg-forest-800 text-cream-50 border-forest-800'
                  : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'
              }`}>
              {t === 'SUBMITTED' && '📬'}
              {t === 'REVISION_REQUESTED' && '🔄'}
              {t === 'GRADED' && '✅'}
              {t}
              {t !== 'ALL' && counts[t] > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === t ? 'bg-white/20' : 'bg-forest-100 text-forest-700'
                }`}>
                  {counts[t]}
                </span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load submissions.</span>
          </div>
        )}

        {!loading && submissions.length === 0 && (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <PenLine size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No submissions</p>
            <p className="text-sm mt-2">
              {activeTab === 'SUBMITTED'
                ? 'No new submissions waiting for your review'
                : activeTab === 'REVISION_REQUESTED'
                  ? 'No submissions awaiting revision'
                  : 'No graded submissions yet'}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 bg-white border border-cream-200 rounded-2xl animate-pulse" />
              ))
            : submissions.map(s => (
                <SubmissionCard
                  key={s.id}
                  submission={s}
                  onGraded={() => setRefetchKey(k => k + 1)}
                />
              ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExpertSubmissionsPage;